import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Users, Ticket, FolderKanban, Briefcase, Plus } from 'lucide-react';
import type { CampaignMode } from '../types/multiInfluencer';
import { useLanguage } from '../contexts/LanguageContext';

interface CampaignModeSelectorProps {
  onSelect: (mode: CampaignMode) => void;
  buttonLabel?: string;
  className?: string;
}

export function CampaignModeSelector({
  onSelect,
  buttonLabel,
  className = '',
}: CampaignModeSelectorProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const label = buttonLabel || t('campaignMode.newCampaign');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (mode: CampaignMode) => {
    setIsOpen(false);
    onSelect(mode);
  };

  const options: {
    mode: CampaignMode;
    title: string;
    description: string;
    icon: typeof Building2;
  }[] = [
    {
      mode: 'brand_action',
      title: t('campaignMode.brandAction'),
      description: t('campaignMode.brandActionDescription'),
      icon: Building2,
    },
    {
      mode: 'influencer_marketing',
      title: t('campaignMode.influencerMarketing'),
      description: t('campaignMode.influencerMarketingDescription'),
      icon: Users,
    },
    {
      mode: 'sponsorship',
      title: t('campaignMode.sponsorship'),
      description: t('campaignMode.sponsorshipDescription'),
      icon: Ticket,
    },
    {
      mode: 'projecto',
      title: 'Projecto',
      description: 'Vários serviços, fornecedores e talento no mesmo projeto, com P&L e faturação',
      icon: FolderKanban,
    },
    {
      mode: 'services',
      title: t('campaignMode.services'),
      description: t('campaignMode.servicesDescription'),
      icon: Briefcase,
    },
  ];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="pressable flex items-center gap-2 px-5 py-2.5 text-sm bg-accent-600 text-white rounded-full hover:bg-accent-700 transition-all font-semibold shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
      >
        <Plus className="w-4 h-4" />
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl ring-1 ring-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200 origin-top-right">
          <div className="px-5 pt-4 pb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {label}
            </p>
          </div>
          <div className="p-2">
            {options.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.mode}
                  onClick={() => handleSelect(option.mode)}
                  className="w-full text-left px-3 py-3 rounded-2xl hover:bg-gray-50 transition-colors group mt-0.5 focus:outline-none focus:bg-gray-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-50 rounded-full ring-1 ring-gray-100 group-hover:bg-emerald-50 group-hover:ring-emerald-100 transition-colors flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-600 group-hover:text-emerald-600 transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-0.5 leading-tight">
                        {option.title}
                      </h3>
                      <p className="text-xs text-gray-500 leading-snug">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
